import React, { useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { COPY } from './copy';

// The consent step before the coach shares anything (FR5, PRD §10.3): who
// the introduction is to, exactly what they'd receive (contact details and
// business basics always; each saved result can be unticked), and nothing
// leaves the platform until the owner presses the button. Status comes
// from the server, so a reload shows the outcome.
export default function ConfirmationSheet({ confirmation, onConfirm, onDecline }) {
  if (confirmation.tool_name === 'share_with_advisor') {
    return <AdvisorBriefSheet confirmation={confirmation} onConfirm={onConfirm} onDecline={onDecline} />;
  }
  return <IntroductionSheet confirmation={confirmation} onConfirm={onConfirm} onDecline={onDecline} />;
}

// Succeedo Phase 5: a brief to the owner's own advisor about one gap. The
// brief is the whole point, so it is always shown and must not be empty;
// the owner edits it and sends exactly what they see.
function AdvisorBriefSheet({ confirmation, onConfirm, onDecline }) {
  const details = confirmation.details || {};
  const advisor = details.advisor || {};
  const [brief, setBrief] = useState(details.brief?.text || '');
  const [state, setState] = useState(null);

  if (confirmation.status === 'ok') {
    return (
      <p className="m-0 inline-flex items-center gap-1.5 text-[13px] font-medium text-teal-ink" data-testid="confirmation-done">
        <CheckCircle2 size={16} /> {COPY.advisorBriefSent(advisor.name)}
      </p>
    );
  }
  if (confirmation.status === 'declined') {
    return <p className="m-0 text-[13px] text-muted-foreground" data-testid="confirmation-declined">{COPY.advisorBriefDeclined}</p>;
  }
  if (confirmation.status !== 'pending_confirmation') return null;

  const act = async (action) => {
    setState('saving');
    try {
      const error = await action();
      setState(error || null);
    } catch (e) {
      setState(COPY.confirmationError);
    }
  };

  return (
    <section
      aria-label={COPY.advisorBriefTitle(advisor.name)}
      data-testid="advisor-brief-sheet"
      className="bg-card border border-succeedo-orange/30 rounded-2xl p-5 flex flex-col gap-4"
    >
      <header className="flex items-start gap-2.5">
        <ShieldCheck size={18} className="text-orange-ink shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-[16px] font-medium text-foreground">{COPY.advisorBriefTitle(advisor.name)}</h3>
          <span className="text-[13px] text-muted-foreground">
            {[advisor.title, advisor.firm].filter(Boolean).join(' · ')}
          </span>
          {details.topic?.label && (
            <span className="text-[13px] text-foreground">{COPY.advisorBriefAbout(details.topic.label)}</span>
          )}
        </div>
      </header>
      <div className="flex flex-col gap-2">
        <label className="text-[13px] text-muted-foreground" htmlFor={`advisor-brief-${confirmation.id}`}>
          {COPY.advisorBriefHint}
        </label>
        <textarea
          id={`advisor-brief-${confirmation.id}`}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={5}
          maxLength={700}
          className="w-full rounded-lg border border-input bg-background p-2 text-[13px] text-foreground"
        />
      </div>
      <p className="m-0 text-[12px] leading-normal text-muted-foreground">{COPY.advisorBriefPrivacy}</p>
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className="min-h-9 px-[18px] rounded-full border-0 bg-orange-cta text-white text-[13px] font-medium cursor-pointer disabled:opacity-60"
          disabled={state === 'saving' || !onConfirm || !brief.trim()}
          onClick={() => act(() => onConfirm(confirmation, [], brief))}
        >
          {state === 'saving' ? COPY.sending : COPY.advisorBriefSend(advisor.name)}
        </button>
        <button
          type="button"
          className="min-h-9 px-[18px] rounded-full border border-border bg-transparent text-foreground text-[13px] cursor-pointer disabled:opacity-60"
          disabled={state === 'saving' || !onDecline}
          onClick={() => act(() => onDecline(confirmation))}
        >
          {COPY.notNow}
        </button>
      </div>
      {state && state !== 'saving' && <span className="text-[12px] text-coral-ink">{state}</span>}
    </section>
  );
}

function IntroductionSheet({ confirmation, onConfirm, onDecline }) {
  const details = confirmation.details || {};
  const partner = details.partner || {};
  const artifacts = details.artifacts || [];
  // Artifact ids: the exact versions this sheet shows are what get shared.
  const [chosen, setChosen] = useState(() => artifacts.filter((a) => a.checked).map((a) => a.id));
  // George P4-4: an optional brief, off until the owner ticks it, editable.
  const draft = details.brief;
  const [sendBrief, setSendBrief] = useState(false);
  const [brief, setBrief] = useState(draft?.text || '');
  const [state, setState] = useState(null); // 'saving' | error message

  if (confirmation.status === 'ok') {
    return (
      <p className="m-0 inline-flex items-center gap-1.5 text-[13px] font-medium text-teal-ink" data-testid="confirmation-done">
        <CheckCircle2 size={16} /> {COPY.introductionSent(partner.name)}
      </p>
    );
  }
  if (confirmation.status === 'declined') {
    return <p className="m-0 text-[13px] text-muted-foreground" data-testid="confirmation-declined">{COPY.introductionDeclined}</p>;
  }
  if (confirmation.status !== 'pending_confirmation') return null;

  const toggle = (id) => setChosen((list) => (list.includes(id) ? list.filter((k) => k !== id) : [...list, id]));
  const act = async (action) => {
    setState('saving');
    try {
      const error = await action();
      setState(error || null);
    } catch (e) {
      setState(COPY.confirmationError);
    }
  };

  return (
    <section
      aria-label={COPY.confirmIntroTitle(partner.name)}
      data-testid="confirmation-sheet"
      className="bg-card border border-succeedo-orange/30 rounded-2xl p-5 flex flex-col gap-4"
    >
      <header className="flex items-start gap-2.5">
        <ShieldCheck size={18} className="text-orange-ink shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-[16px] font-medium text-foreground">{COPY.confirmIntroTitle(partner.name)}</h3>
          {partner.org_name && <span className="text-[13px] text-muted-foreground">{partner.org_name}</span>}
        </div>
      </header>

      <div className="flex flex-col gap-2">
        <span className="font-label text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
          {COPY.whatTheyReceive(partner.name)}
        </span>
        <ul className="m-0 p-0 list-none flex flex-col">
          {(details.fields || []).map((field) => (
            <li key={field.key} className="flex justify-between gap-4 py-2 border-t border-border/60 text-[13px]">
              <span className="text-muted-foreground">{field.label}</span>
              <span className="text-foreground text-right break-all">{field.value}</span>
            </li>
          ))}
        </ul>
      </div>

      {artifacts.length > 0 && (
        <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
          <legend className="font-label text-[10px] uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
            {COPY.resultsToShare}
          </legend>
          {artifacts.map((artifact) => (
            <label key={artifact.kind} className="flex items-start gap-2.5 py-1.5 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 accent-[hsl(var(--succeedo-orange))]"
                checked={chosen.includes(artifact.id)}
                onChange={() => toggle(artifact.id)}
              />
              <span className="flex flex-col">
                <span className="text-foreground">{artifact.title}</span>
                {artifact.summary && <span className="text-muted-foreground">{artifact.summary}</span>}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {draft && (
        <div className="flex flex-col gap-2" data-testid="brief-section">
          <label className="flex items-start gap-2.5 text-[13px] cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 accent-[hsl(var(--succeedo-orange))]"
              checked={sendBrief}
              onChange={() => setSendBrief(!sendBrief)}
            />
            <span className="flex flex-col">
              <span className="text-foreground">{COPY.briefLabel(partner.name)}</span>
              <span className="text-muted-foreground">{COPY.briefHint}</span>
            </span>
          </label>
          {sendBrief && (
            <>
              <label className="sr-only" htmlFor={`brief-${confirmation.id}`}>{COPY.briefLabel(partner.name)}</label>
              <textarea
                id={`brief-${confirmation.id}`}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={5}
                maxLength={700}
                className="w-full rounded-lg border border-input bg-background p-2 text-[13px] text-foreground"
              />
              <span className="text-[12px] text-muted-foreground">{COPY.briefCannotRecall}</span>
            </>
          )}
        </div>
      )}

      <p className="m-0 text-[12px] leading-normal text-muted-foreground">{COPY.nothingSharedYet}</p>

      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className="min-h-9 px-[18px] rounded-full border-0 bg-orange-cta text-white text-[13px] font-medium cursor-pointer disabled:opacity-60"
          disabled={state === 'saving' || !onConfirm}
          onClick={() => act(() => onConfirm(confirmation, chosen, sendBrief && brief.trim() ? brief : null))}
        >
          {state === 'saving' ? COPY.sending : COPY.confirmIntro}
        </button>
        <button
          type="button"
          className="min-h-9 px-[18px] rounded-full border border-border bg-transparent text-foreground text-[13px] cursor-pointer disabled:opacity-60"
          disabled={state === 'saving' || !onDecline}
          onClick={() => act(() => onDecline(confirmation))}
        >
          {COPY.notNow}
        </button>
      </div>
      {state && state !== 'saving' && <span className="text-[12px] text-coral-ink">{state}</span>}
    </section>
  );
}
